import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ComplianceDashboard = ({ 
  scanResults, 
  violations, 
  loading, 
  onManualScan, 
  autoScanEnabled, 
  onToggleAutoScan,
  lastScanTime 
}) => {
  const getComplianceScore = () => {
    if (!violations || violations?.length === 0) return 100;
    const criticalCount = violations?.filter(v => v?.severity === 'critical')?.length;
    const highCount = violations?.filter(v => v?.severity === 'high')?.length;
    const score = Math.max(0, 100 - (criticalCount * 10) - (highCount * 5));
    return Math.round(score);
  };

  const getRiskLevel = () => {
    const score = getComplianceScore();
    if (score >= 95) return { level: 'Low', color: 'text-success', bg: 'bg-success/10' };
    if (score >= 85) return { level: 'Medium', color: 'text-warning', bg: 'bg-warning/10' };
    return { level: 'High', color: 'text-error', bg: 'bg-error/10' };
  };

  const complianceScore = getComplianceScore();
  const riskLevel = getRiskLevel();
  const criticalViolations = violations?.filter(v => v?.severity === 'critical')?.length || 0;
  const pendingRemediation = violations?.filter(v => v?.violationStatus === 'identified')?.length || 0;

  return (
    <div className="space-y-4">
      {/* Real-time Compliance Score */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-base text-foreground">Compliance Score</h3>
          <Icon name="Shield" size={20} color="var(--color-primary)" />
        </div>
        <div className="text-center">
          <div className={`text-5xl font-bold mb-2 ${
            complianceScore >= 95 ? 'text-success' : 
            complianceScore >= 85 ? 'text-warning' : 'text-error'
          }`}>
            {complianceScore}%
          </div>
          <p className="text-sm text-muted-foreground">Overall Compliance</p>
        </div>
      </div>
      {/* Violation Risk Indicators */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-heading font-semibold text-base text-foreground mb-4">Risk Indicators</h3>
        <div className="space-y-3">
          <div className={`flex items-center justify-between p-3 rounded-lg ${riskLevel?.bg}`}>
            <div className="flex items-center gap-2">
              <Icon name="AlertTriangle" size={16} color={`var(--color-${riskLevel?.color?.replace('text-', '')})`} />
              <span className="text-sm font-medium text-foreground">Risk Level</span>
            </div>
            <span className={`text-sm font-bold ${riskLevel?.color}`}>{riskLevel?.level}</span>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-error/10">
            <div className="flex items-center gap-2">
              <Icon name="XCircle" size={16} color="var(--color-error)" />
              <span className="text-sm font-medium text-foreground">Critical Violations</span>
            </div>
            <span className="text-sm font-bold text-error">{criticalViolations}</span>
          </div>
          <div className="flex items-center justify-between p-3 rounded-lg bg-warning/10">
            <div className="flex items-center gap-2">
              <Icon name="Clock" size={16} color="var(--color-warning)" />
              <span className="text-sm font-medium text-foreground">Pending Remediation</span>
            </div>
            <span className="text-sm font-bold text-warning">{pendingRemediation}</span>
          </div>
        </div>
      </div>
      {/* Automated Scan Status */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-base text-foreground">Scan Status</h3>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${
              autoScanEnabled ? 'bg-success animate-pulse' : 'bg-muted-foreground'
            }`} />
            <span className="text-xs text-muted-foreground">
              {autoScanEnabled ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        
        {lastScanTime && (
          <p className="text-xs text-muted-foreground mb-4">
            Last scan: {new Date(lastScanTime)?.toLocaleTimeString()}
          </p>
        )}

        <div className="space-y-2">
          <Button
            variant="primary"
            size="sm"
            onClick={onManualScan}
            disabled={loading}
            iconName={loading ? 'Loader2' : 'Play'}
            className="w-full"
          >
            {loading ? 'Scanning...' : 'Run Manual Scan'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleAutoScan}
            iconName={autoScanEnabled ? 'PauseCircle' : 'PlayCircle'}
            className="w-full"
          >
            {autoScanEnabled ? 'Disable Auto-Scan' : 'Enable Auto-Scan'}
          </Button>
        </div>
      </div>
      {/* AI Analysis Summary */}
      {scanResults && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Icon name="Sparkles" size={18} color="var(--color-primary)" />
            <h3 className="font-heading font-semibold text-base text-foreground">AI Analysis</h3>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Critical Issues</span>
              <span className="font-bold text-error">
                {scanResults?.criticalViolations?.length || 0}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Deadline Alerts</span>
              <span className="font-bold text-warning">
                {scanResults?.deadlineSensitiveIssues?.length || 0}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Risk Level</span>
              <span className={`font-bold ${
                scanResults?.overallRiskLevel === 'high' ? 'text-error' :
                scanResults?.overallRiskLevel === 'medium' ? 'text-warning' : 'text-success'
              }`}>
                {scanResults?.overallRiskLevel?.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplianceDashboard;